import {ChartType, ProjectFeatureFlag, REPORT_LIMITS, ReportName} from 'common/report/constants';
import {CurrentProject} from 'common/report/model-resources/projects';
import {
  computeMetricClauseByKey,
  computeMetricUnit,
  getPluralizedMetricUnit,
} from 'common/report/insights/util/tooltip';
import {
  FlatSegment,
  InsightsBookmarkParams,
  InsightsQueryResults,
  ReportSectionsMetadata,
} from 'common/report/insights/util/types';
import {getFlatSegments, getSegmentLookupMap, getSortedKeyMapForFlatSegments} from 'common/report/insights/util';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {MetricSegment} from 'common/charts/visualization/multi-metric/types';

export interface InsightsMetricLabels {
  metricName: string;
  metricUnit?: string;
}

export interface InsightsMetricData {
  segmentLookup: Map<string, FlatSegment>;
  timeComparisonLookup: Map<string, FlatSegment>;
  liftComparisonLookup: Map<string, FlatSegment>;

  // Insights uses a special index with a stable sort order (independent from the bookmark sort order) to store selected legend segments
  legendIdToIndexMap: {[id: string]: number};
  legendIndexToId: string[];
  /** holds positioning of the popover for the active highcharts point */
  popoverStyles?: {
    visibility?: string;
    left?: string;
    top?: string;
  } | null;
  popoverSegment?: MetricSegment | null;
}

export function getMetricData(results: InsightsQueryResults): InsightsMetricData {
  const segments = getFlatSegments(results.headers, results.series);
  const segmentLookup = getSegmentLookupMap(segments);

  let timeComparisonLookup = null;
  if (results.time_comparison) {
    const timeComparisonSegments = getFlatSegments(results.headers, results.time_comparison.series);
    timeComparisonLookup = getSegmentLookupMap(timeComparisonSegments);
  }

  let liftComparisonLookup = null;
  if (results.lift_comparison) {
    const liftComparisonSegments = getFlatSegments(results.headers, results.lift_comparison.series);
    liftComparisonLookup = getSegmentLookupMap(liftComparisonSegments);
  }

  const legendIdToIndexMap = getSortedKeyMapForFlatSegments(segments);

  const legendIndexToId: string[] = [];
  Object.keys(legendIdToIndexMap).forEach((id) => (legendIndexToId[legendIdToIndexMap[id]] = id));

  return {segmentLookup, legendIdToIndexMap, legendIndexToId, timeComparisonLookup, liftComparisonLookup};
}

export function getMetricLabels({
  bookmarkParams,
  sectionsMetadata,
  dataGroups,
  count,
  key,
}: {
  bookmarkParams: InsightsBookmarkParams;
  sectionsMetadata: ReportSectionsMetadata;
  dataGroups: DataGroup[];
  count: number;
  key: string;
}): InsightsMetricLabels {
  const metricClauseByKey = computeMetricClauseByKey({
    sectionsMetadata,
    bookmarkParams,
    dataGroups,
  });

  const metricClause = metricClauseByKey[key];
  const metricName = metricClause.metricKey;

  const formattedMetricUnit = computeMetricUnit(metricClause);
  const metricUnit: string = formattedMetricUnit ? getPluralizedMetricUnit(count, formattedMetricUnit) : ``;

  return {metricName, metricUnit};
}

// TODO: Remove this flag and function after top n option is exposed in product
export function getDefaultSelectedSegmentLimit(currentProject: CurrentProject) {
  return currentProject?.hasFeatureFlag(ProjectFeatureFlag.InsightsMetricOverrideDefaultSelected)
    ? 10
    : REPORT_LIMITS[ReportName.Insights].segments[ChartType.Metric].default;
}
